// Phase 3A — "Next in your journey" surface.
//
// A quiet, inline, non-modal rail rendered after content. Never a popup, never
// covers content, never repeats an ask the visitor has already completed.
// Height is reserved by the grid so nothing shifts when journey state resolves.
import { useEffect, useRef } from "react";
import { Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { useJourney } from "@/hooks/use-journey";
import { nextSteps, type JourneyKind, type JourneyAction } from "@/lib/journey";
import { track } from "@/lib/analytics";

export function JourneyLink({
  action,
  variant = "secondary",
  surface,
  className = "",
}: {
  action: JourneyAction;
  variant?: "primary" | "secondary" | "quiet";
  surface: string;
  className?: string;
}) {
  const base =
    "inline-flex min-h-11 items-center justify-center gap-2 rounded-full px-6 py-3 text-sm transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold";
  const styles =
    variant === "primary"
      ? "bg-gold-gradient font-semibold text-obsidian shadow-gold hover:shadow-halo"
      : variant === "secondary"
        ? "border border-gold text-gold-luminous hover:bg-gold hover:text-obsidian"
        : "text-gold hover:text-gold-luminous";

  return (
    <Link
      to={action.to as never}
      params={action.params as never}
      onClick={() =>
        track(variant === "primary" ? "journey_primary_cta" : "journey_secondary_cta", {
          action: action.id,
          stage: action.stage,
          surface,
        })
      }
      className={`${base} ${styles} ${className}`}
    >
      {action.label}
      <ArrowRight size={14} aria-hidden />
    </Link>
  );
}

export function NextStep({
  kind,
  heading = "Next in your journey",
  gateN,
  releaseSlug,
  conceptSlug,
}: {
  kind: JourneyKind;
  heading?: string;
  gateN?: number;
  releaseSlug?: string;
  conceptSlug?: string;
}) {
  const journey = useJourney();
  const steps = nextSteps(kind, journey.visitor, {
    gateN,
    releaseSlug,
    relatedConceptSlug: conceptSlug,
  });
  const logged = useRef(false);

  useEffect(() => {
    if (logged.current || journey.loading) return;
    logged.current = true;
    track("journey_next_step_view", { kind, stage: journey.stage, count: steps.length });
  }, [journey.loading, journey.stage, kind, steps.length]);

  if (steps.length === 0) return null;

  return (
    <section aria-labelledby={`next-step-${kind}`} className="mx-auto max-w-6xl px-4 py-12 sm:px-6">
      <p className="text-[10px] uppercase tracking-[0.35em] text-gold">Continue</p>
      <h2 id={`next-step-${kind}`} className="mt-2 font-serif text-2xl text-ivory">
        {heading}
      </h2>
      <ul className="mt-6 grid gap-3 sm:grid-cols-3">
        {steps.map((s) => (
          <li key={s.id}>
            <Link
              to={s.to as never}
              params={s.params as never}
              onClick={() =>
                track("journey_next_step_click", { action: s.id, stage: s.stage, kind })
              }
              className="group flex h-full min-h-[104px] flex-col justify-between rounded-2xl border hairline bg-obsidian/60 p-5 transition-all hover:border-gold hover:bg-obsidian/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold"
            >
              <span className="font-serif text-lg text-ivory group-hover:text-gold-luminous">
                {s.label}
              </span>
              {s.note ? <span className="mt-2 text-sm text-muted-foreground">{s.note}</span> : null}
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
